import { motion } from 'framer-motion';
import { Link } from '@/components/LocalizedLink';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/contexts/LanguageContext';
import { fetchSettingsContent } from '@/lib/contentService';
import { COOKIE_POLICY_PATH, openCookiePreferences } from '@/lib/cookieConsent';
import { DELIVERY_REFUND_PATH, DISTANCE_SALES_PATH, LEGAL_CONTACT_EMAIL } from '@/lib/legalContent';
import { getVisibleEntityLinks } from '@/lib/entityProfiles';
import {
  BUSINESS_LOCATION,
  formatBusinessAddress,
  formatBusinessLegalFooter,
} from '@/lib/businessLocation';
import { CONTACT_PHONE_DISPLAY, CONTACT_PHONE_E164 } from '@/lib/contactDetails';
import { getFeaturedServiceSlugs, getServicesBySlugs, localizeServiceText } from '@/lib/serviceCatalog';
import { PremiumDivider } from '@/components/ui/spotlight-card';

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const Footer = () => {
  const { t, lang } = useLanguage();
  const { data: settings } = useQuery({
    queryKey: ['singleton', 'settings'],
    queryFn: fetchSettingsContent,
  });

  const baseFooterLinks =
    settings?.footerGroups?.flatMap((group) => group.links) ??
    [
      { label: t('nav.services'), href: '/services' },
      { label: t('nav.projects'), href: '/projects' },
      { label: t('nav.tools'), href: '/tools' },
      { label: t('nav.blog'), href: '/blog' },
      { label: t('nav.about'), href: '/about' },
      { label: t('nav.contact'), href: '/contact' },
    ];

  const entityLinks = getVisibleEntityLinks(lang);
  const discoveryLinks = [
    { label: t('nav.tools'), href: '/tools' },
    { label: t('footer.aiAnswers'), href: '/ai-answers' },
    { label: lang === 'tr' ? 'Rehberler' : 'Guides', href: '/guides' },
  ];
  const commercialLinks = getServicesBySlugs(getFeaturedServiceSlugs()).map((service) => ({
    label: localizeServiceText(service.shortLabel, lang),
    href: `/services/${service.slug}`,
  }));

  const legalLinks = [
    { label: t('footer.privacy'), href: '/privacy' },
    { label: t('footer.terms'), href: '/terms' },
    { label: t('footer.kvkk'), href: '/kvkk-aydinlatma-metni' },
    { label: t('footer.deliveryRefund'), href: DELIVERY_REFUND_PATH },
    { label: lang === 'tr' ? 'Mesafeli Satış Sözleşmesi' : 'Distance Sales Agreement', href: DISTANCE_SALES_PATH },
    { label: lang === 'tr' ? 'Çerez Politikası' : 'Cookie Policy', href: COOKIE_POLICY_PATH },
  ];

  const columns = [
    {
      heading: lang === 'tr' ? 'Stüdyo' : 'Studio',
      links: baseFooterLinks,
    },
    {
      heading: lang === 'tr' ? 'Keşfet' : 'Discover',
      links: discoveryLinks,
    },
    {
      heading: lang === 'tr' ? 'Hizmetler' : 'Services',
      links: commercialLinks,
    },
    {
      heading: lang === 'tr' ? 'Yasal' : 'Legal',
      links: legalLinks,
    },
  ];

  return (
    <footer className="relative overflow-hidden border-t border-border/50 bg-background">
      <div className="container mx-auto px-6 lg:px-12 pt-12 pb-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10"
        >
          <motion.div variants={itemVariants} className="lg:col-span-4 flex flex-col gap-6">
            <Link to="/" className="inline-flex items-center gap-2.5 group">
              <span className="w-2 h-2 rounded-full bg-foreground transition-transform duration-300 group-hover:scale-125" />
              <span className="font-display text-base font-semibold text-foreground tracking-tight">figtures</span>
            </Link>
            <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-sm">
              {t('footer.tagline')}
            </p>
            <p className="font-body text-xs text-muted-foreground/80 leading-relaxed max-w-md">
              {formatBusinessLegalFooter(lang)}
            </p>
            <div className="font-body text-sm text-muted-foreground space-y-2">
              <p className="leading-relaxed">{formatBusinessAddress(lang)}</p>
              <a
                href={`mailto:${LEGAL_CONTACT_EMAIL}`}
                className="block hover:text-foreground transition-colors"
              >
                {LEGAL_CONTACT_EMAIL}
              </a>
              <a
                href={`tel:${CONTACT_PHONE_E164}`}
                className="block hover:text-foreground transition-colors"
              >
                {CONTACT_PHONE_DISPLAY}
              </a>
              <a
                href={BUSINESS_LOCATION.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-xs tracking-wide uppercase hover:text-foreground transition-colors"
              >
                {lang === 'tr' ? 'Google Haritalar →' : 'Google Maps →'}
              </a>
            </div>
            {entityLinks.length > 0 && (
              <div>
                <p className="font-body text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">
                  {t('footer.entityProfiles')}
                </p>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {entityLinks.map((item) => (
                    <a
                      key={`${item.key}-${item.url}`}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-body text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-8">
            {columns.map((col) => (
              <motion.div key={col.heading} variants={itemVariants} className="flex flex-col gap-4">
                <p className="font-body text-[10px] tracking-[0.2em] uppercase text-foreground/80">
                  {col.heading}
                </p>
                <ul className="space-y-3">
                  {col.links.map((item) => (
                    <li key={`${col.heading}-${item.label}-${item.href}`}>
                      <Link
                        to={item.href}
                        className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors duration-300"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="mt-12 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-body text-xs text-muted-foreground/80">
            {lang === 'tr'
              ? 'Kartlı ödemeler iyzico güvencesiyle, 3D Secure ile alınır.'
              : 'Card payments are processed securely by iyzico with 3D Secure.'}
          </p>
          <img
            src="/payment/iyzico-logo-band-colored.svg"
            alt={lang === 'tr' ? 'iyzico ile Öde, Mastercard, Visa, American Express, Troy' : 'Pay with iyzico, Mastercard, Visa, American Express, Troy'}
            width={429}
            height={32}
            loading="lazy"
            className="h-6 w-auto max-w-full sm:h-7 dark:hidden"
          />
          <img
            src="/payment/iyzico-logo-band-white.svg"
            alt={lang === 'tr' ? 'iyzico ile Öde, Mastercard, Visa, American Express, Troy' : 'Pay with iyzico, Mastercard, Visa, American Express, Troy'}
            width={456}
            height={32}
            loading="lazy"
            className="hidden h-6 w-auto max-w-full sm:h-7 dark:block"
          />
        </div>

        <PremiumDivider className="my-12" />

        <motion.p
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="pointer-events-none select-none mb-10 text-center font-display font-bold text-transparent [--stroke:hsl(var(--foreground)/0.12)]"
          style={{
            fontSize: 'clamp(3.5rem, 14vw, 11rem)',
            WebkitTextStroke: '1px var(--stroke)',
            lineHeight: 0.9,
          }}
          aria-hidden
        >
          figtures
        </motion.p>

        <motion.div
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
        >
          <p className="font-body text-xs text-muted-foreground/70">{t('footer.rights')}</p>
          <button
            type="button"
            onClick={openCookiePreferences}
            className="font-body text-xs text-muted-foreground/70 hover:text-foreground transition-colors text-left md:text-right"
          >
            {t('footer.cookieSettings')}
          </button>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
